import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { selectPlan } from '@core/store/plan/plan.selectors';

/**
 * Service that provides context-aware navigation methods for entity operations.
 *
 * This service automatically includes plan context in URLs when the user is operating
 * within a plan. It reads the current plan from the NgRx store and builds URLs with the
 * appropriate structure:
 *
 * - Global context: `/ds/:dsId/data/:entity/...`
 * - Plan context: `/ds/:dsId/planning-spaces/:psId/plan/:planId/data/:entity/...`
 *
 * ## Benefits
 *
 * - **Automatic context detection**: Components don't need routing logic
 * - **Consistent URL structure**: All navigation follows the same pattern
 * - **Type safety**: Centralized methods prevent navigation errors
 * - **Testability**: Navigation logic can be mocked and tested
 *
 * @example
 *   // In a component
 *   constructor() {
 *   private navService = inject(PlanAwareNavigationService);
 *   }
 *
 *   editEntity() {
 *   // Automatically includes plan context if present
 *   this.navService.navigateToEntityEdit(dataspaceId, 'LOCATION', 'uid-123');
 *   }
 */
@Injectable({ providedIn: 'root' })
export class PlanAwareNavigationService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly planContext = this.store.selectSignal(selectPlan);

  /**
   * Build URL path for entity operations, automatically including plan context if present.
   *
   * @example
   *   // Global context
   *   buildEntityPath(1, 'LOCATION', 'edit', ['uid-123', 'form']);
   *   // => ['/ds', '1', 'data', 'LOCATION', 'edit', 'uid-123', 'form']
   *
   *   // Plan context (when plan is in store)
   *   buildEntityPath(1, 'LOCATION', 'edit', ['uid-123', 'form']);
   *   // => ['/ds', '1', 'planning-spaces', '456', 'plan', '789', 'data', 'LOCATION', 'edit', 'uid-123', 'form']
   *
   * @param dataspaceId - The dataspace ID
   * @param entityType - The entity type (e.g., 'LOCATION', 'SITE')
   * @param operation - Optional operation: 'new', 'edit', 'upload', 'custom-gen'
   * @param additionalSegments - Additional path segments (e.g., entityUid, mode)
   * @returns Array of URL segments for router.navigate()
   */
  buildEntityPath(
    dataspaceId: number | string,
    entityType: string,
    operation?: 'new' | 'edit' | 'upload' | 'custom-gen',
    additionalSegments?: string[]
  ): string[] {
    const plan = this.planContext();

    const basePath = plan
      ? [
          '/ds',
          String(dataspaceId),
          'planning-spaces',
          plan.planningSpace.id,
          'plan',
          plan.id,
          'data',
          entityType
        ]
      : ['/ds', String(dataspaceId), 'data', entityType];

    if (operation) {
      basePath.push(operation);
    }

    if (additionalSegments) {
      basePath.push(...additionalSegments);
    }

    return basePath;
  }

  /**
   * Navigate to entity edit page (context-aware).
   *
   * @example
   *   navigateToEntityEdit(1, 'LOCATION', 'uid-123', 'form');
   *
   * @param dataspaceId - The dataspace ID
   * @param entityType - The entity type
   * @param entityUid - The entity UID to edit
   * @param mode - Editor mode: 'form' or 'json' (default: 'form')
   * @returns Promise that resolves when navigation completes
   */
  navigateToEntityEdit(
    dataspaceId: number | string,
    entityType: string,
    entityUid: string,
    mode: 'form' | 'json' = 'form'
  ): Promise<boolean> {
    const path = this.buildEntityPath(dataspaceId, entityType, 'edit', [entityUid, mode]);
    return this.router.navigate(path);
  }

  /**
   * Navigate to entity list (context-aware).
   *
   * @example
   *   navigateToEntityList(1, 'LOCATION');
   *
   * @param dataspaceId - The dataspace ID
   * @param entityType - The entity type
   * @returns Promise that resolves when navigation completes
   */
  navigateToEntityList(dataspaceId: number | string, entityType: string): Promise<boolean> {
    const path = this.buildEntityPath(dataspaceId, entityType);
    return this.router.navigate(path);
  }

  /**
   * Navigate to new entity form (context-aware).
   *
   * @example
   *   navigateToNewEntity(1, 'LOCATION', 'form');
   *
   * @param dataspaceId - The dataspace ID
   * @param entityType - The entity type
   * @param mode - Editor mode: 'form' or 'json' (default: 'form')
   * @returns Promise that resolves when navigation completes
   */
  navigateToNewEntity(
    dataspaceId: number | string,
    entityType: string,
    mode: 'form' | 'json' = 'form'
  ): Promise<boolean> {
    const path = this.buildEntityPath(dataspaceId, entityType, 'new', [mode]);
    return this.router.navigate(path);
  }
}
